import React, { useCallback, useEffect, useMemo } from "react";
import { Flex, Text, Icon, Popover, PopoverTrigger, PopoverContent, PopoverBody, Button } from "design-system";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { useLocation } from "react-router";

import { createMessage, HEADER_TITLES } from "@appsmith/constants/messages";
import { setIdeEditorPagesActiveStatus } from "actions/ideActions";
import { getPagesActiveStatus } from "selectors/ideSelectors";
import { selectAllPages } from "@appsmith/selectors/entitiesSelector";
import type { Page } from "@appsmith/constants/ReduxActionConstants";
import { PageElement } from "pages/Editor/IDE/EditorPane/components/PageElement";
import PaneHeader from "../LeftPane/PaneHeader";
import { getHasCreatePagePermission } from "@appsmith/utils/BusinessFeatures/permissionPageHelpers";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";
import { FEATURE_FLAG } from "@appsmith/entities/FeatureFlag";
import { getCurrentApplication } from "@appsmith/selectors/applicationSelectors";
import type { AppState } from "@appsmith/reducers";
import { getCurrentApplicationId } from "selectors/editorSelectors";
import { getNextEntityName } from "utils/AppsmithUtils";
import { createNewPageFromEntities } from "actions/pageActions";
import { getCurrentWorkspaceId } from "@appsmith/selectors/selectedWorkspaceSelectors";
import { getInstanceId } from "@appsmith/selectors/tenantSelectors";
import AddPageContextMenu from "pages/Editor/Explorer/Pages/AddPageContextMenu";
// no outside click hook needed when using Popover

const ListScrollArea = styled.div`
  overflow-y: auto;
  max-height: 300px;
`;

const EditorTitle = ({ title }: { title: string }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const active = useSelector(getPagesActiveStatus);
  const pages: Page[] = useSelector(selectAllPages);

  // Permissions for add page button (to align with PagesSection)
  const isFeatureEnabled = useFeatureFlag(FEATURE_FLAG.license_gac_enabled);
  const applicationId = useSelector(getCurrentApplicationId);
  const userAppPermissions = useSelector(
    (state: AppState) => getCurrentApplication(state)?.userPermissions ?? [],
  );
  const canCreatePages = getHasCreatePagePermission(
    isFeatureEnabled,
    userAppPermissions,
  );
  const workspaceId = useSelector(getCurrentWorkspaceId);
  const instanceId = useSelector(getInstanceId);

  // We rely on Popover's onOpenChange to control open state

  // Close on route change
  useEffect(() => {
    if (active) dispatch(setIdeEditorPagesActiveStatus(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const createPageCallback = useCallback(() => {
    const name = getNextEntityName(
      "Page",
      pages.map((p) => p.pageName),
    );
    dispatch(
      createNewPageFromEntities(
        applicationId,
        name,
        workspaceId,
        false,
        instanceId,
      ),
    );
  }, [dispatch, pages, applicationId, workspaceId, instanceId]);

  const pageElements = useMemo(
    () => pages.map((page) => <PageElement key={page.pageId} page={page} />),
    [pages],
  );

  return (
    <Popover
      onOpenChange={(open) => {
        dispatch(setIdeEditorPagesActiveStatus(!!open));
      }}
      open={active}
    >
      <PopoverTrigger>
        <Button kind="tertiary" onClick={() => dispatch(setIdeEditorPagesActiveStatus(!active))} size="md">
          <Flex
            alignItems={"center"}
            borderRadius={"var(--ads-v2-border-radius)"}
            className={"hover:bg-[var(--ads-v2-color-bg-subtle)] cursor-pointer"}
            justifyContent={"center"}
            p={"spaces-2"}
          >
            <Text
              color={"var(--ads-v2-colors-content-label-inactive-fg)"}
              kind="body-m"
            >
              {createMessage(HEADER_TITLES.EDITOR) + " /"}
            </Text>
            <Flex
              alignItems={"center"}
              className={"t--pages-switcher"}
              data-active={active}
              gap={"spaces-1"}
              height={"100%"}
              justifyContent={"center"}
              paddingLeft={"spaces-2"}
            >
              <Text isBold kind={"body-m"}>
                {title}
              </Text>
              <Icon
                name={active ? "arrow-up-s-line" : "arrow-down-s-line"}
                size={"md"}
              />
            </Flex>
          </Flex>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="z-[1000] overflow-hidden !p-0" side="bottom" sideOffset={4} size="sm">
        <PopoverBody className="!p-0 !overflow-visible">
          <PaneHeader
            className="pages"
            rightIcon={
              canCreatePages ? (
                <AddPageContextMenu
                  buttonSize="sm"
                  className="group pages"
                  createPageCallback={createPageCallback}
                  onMenuClose={() => {}}
                  openMenu={false}
                />
              ) : null
            }
            title={`All Pages (${pages.length})`}
          />
          <ListScrollArea>
            <Flex
              alignItems={"center"}
              flex={"1"}
              flexDirection={"column"}
              overflow={"auto"}
              width={"100%"}
            >
              {pageElements}
            </Flex>
          </ListScrollArea>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export { EditorTitle };
